"use strict";

/**
 * src/pages/analysis/analysisSelfRegistrationClient.js
 *
 * SELF_REGISTRATION_FRONTEND_2 — Frontend client for the backend's
 * self-registration Basic-trial bootstrap route.
 *
 * Exposes exactly:
 *   SELF_REGISTRATION_BOOTSTRAP_ENDPOINT
 *   bootstrapSelfRegistrationTrial(user, options?)
 *   shouldBootstrapSelfRegistration(claims)
 *   SelfRegistrationBootstrapError
 *
 * This module is CommonJS (matches the existing CJS pattern used by
 * other src/pages/analysis/*.js modules, e.g.
 * telegramNotificationSettingsApi.js) so it can be require()'d
 * directly by a Node self-check without any build step, while still
 * being consumable from the webpack/CRA frontend bundle exactly like
 * those other CJS modules already are.
 *
 * Dependency injection: bootstrapSelfRegistrationTrial(user, { fetchFn })
 * accepts an optional injected fetchFn, which lets
 * analysisSelfRegistrationClientSelfcheck.js exercise every branch
 * below fully offline — no real Firebase call, no real network call,
 * ever.
 *
 * This module deliberately does NOT:
 *   - use the Firebase Admin SDK or the Firestore Client SDK,
 *   - send a request body (the backend route requires an EMPTY body;
 *     this module never sends uid/email/role/plan/expiresAt/access/
 *     status or anything else),
 *   - log a token, an email, or a password (no console.* calls at
 *     all),
 *   - normalize a missing `plan` claim into "basic" — a missing plan
 *     and an explicit non-empty plan are treated as distinct signals.
 */

const SELF_REGISTRATION_BOOTSTRAP_ENDPOINT = "/api/auth/self-registration/bootstrap";

const CACHE_CONTROL_NO_STORE = "no-store";

/** Custom-claims "creator" markers — presence of any means an admin already manages this account. */
const CREATOR_CLAIM_FIELDS = [
  "createdByUid",
  "createdByEmail",
  "createdByRole",
  "createdByAdminUid",
  "createdByAdminEmail",
  "createdByAdminRole",
];

// =========================================================================
// Generic helpers (no side effects)
// =========================================================================

function isPlainObject(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch (_e) {
    return false;
  }
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

// =========================================================================
// Error contract
// =========================================================================

/**
 * Public error shape: only `name`, `message`, `status`, `code` are
 * ever set on instances of this class. Never carries a token,
 * Firebase user, response object, request headers/body, or server
 * stack trace.
 */
class SelfRegistrationBootstrapError extends Error {
  constructor({ status, code, message } = {}) {
    super(isNonEmptyString(message) ? message : "Request failed");
    this.name = "SelfRegistrationBootstrapError";
    this.status = typeof status === "number" && Number.isFinite(status) ? status : 0;
    this.code = isNonEmptyString(code) ? code : "request_failed";
  }
}

function buildInvalidResponseError(httpStatus) {
  return new SelfRegistrationBootstrapError({
    status: httpStatus,
    code: "invalid_response",
    message: "Received an invalid response from the server.",
  });
}

/**
 * Builds a SelfRegistrationBootstrapError from a non-2xx HTTP
 * response, reading only the whitelisted `error`/`message` fields
 * from the (already backend-sanitized) parsed JSON body, if any.
 */
function buildApiErrorFromResponse(httpStatus, parsedBody) {
  const body = isPlainObject(parsedBody) ? parsedBody : {};
  const code = isNonEmptyString(body.error) ? body.error : "request_failed";
  const message = isNonEmptyString(body.message) ? body.message : "Request failed";
  return new SelfRegistrationBootstrapError({ status: httpStatus, code, message });
}

// =========================================================================
// Default dependencies (resolved lazily at call-time, never at require-time)
// =========================================================================

/**
 * Default fetchFn: resolves the browser-global `fetch` at CALL-time
 * (every invocation looks it up fresh), never at require-time.
 */
function defaultFetchFn(...args) {
  const fetchImpl = typeof fetch === "function" ? fetch : null;
  if (typeof fetchImpl !== "function") {
    throw new Error("global fetch is not available in this environment");
  }
  return fetchImpl(...args);
}

/**
 * Parses a fetch-like Response's JSON body safely — returns null on a
 * missing body, an empty body, or a body that isn't valid JSON.
 *
 * @param {*} response - a fetch-like Response object (or fake)
 * @returns {Promise<*>}
 */
async function safeParseJson(response) {
  if (!response || typeof response.json !== "function") {
    return null;
  }
  try {
    return await response.json();
  } catch (_e) {
    return null;
  }
}

// =========================================================================
// bootstrapSelfRegistrationTrial
// =========================================================================

/**
 * Calls POST /api/auth/self-registration/bootstrap with the given
 * Firebase user's ID token and, on success, force-refreshes that
 * user's ID token so freshly-granted custom claims are visible to the
 * caller immediately afterwards.
 *
 * Never sends a request body. Never sends uid/email/role/plan/
 * expiresAt/access/status. Never uses the Firestore Client SDK.
 *
 * @param {{getIdToken: Function}} user - a Firebase Auth user (or fake)
 * @param {{fetchFn?: Function}} [options] - optional injected fetchFn for offline self-check
 * @returns {Promise<{ok: true, bootstrapStatus: string, plan: (string|null), role: (string|null), access: (object|null)}>}
 */
async function bootstrapSelfRegistrationTrial(user, options) {
  if (!user || typeof user !== "object" || typeof user.getIdToken !== "function") {
    throw new SelfRegistrationBootstrapError({
      status: 401,
      code: "invalid_user",
      message: "A signed-in Firebase user is required.",
    });
  }

  const opts = isPlainObject(options) ? options : {};
  const fetchFn = typeof opts.fetchFn === "function" ? opts.fetchFn : defaultFetchFn;

  let token;
  try {
    token = await user.getIdToken();
  } catch (_e) {
    throw new SelfRegistrationBootstrapError({
      status: 401,
      code: "firebase_auth_required",
      message: "Firebase authentication is required.",
    });
  }

  if (!isNonEmptyString(token)) {
    throw new SelfRegistrationBootstrapError({
      status: 401,
      code: "firebase_auth_required",
      message: "Firebase authentication is required.",
    });
  }

  let response;
  try {
    response = await fetchFn(SELF_REGISTRATION_BOOTSTRAP_ENDPOINT, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: CACHE_CONTROL_NO_STORE,
    });
  } catch (_e) {
    throw new SelfRegistrationBootstrapError({
      status: 0,
      code: "network_error",
      message: "Network request failed.",
    });
  }

  if (!response || typeof response !== "object" || typeof response.ok !== "boolean") {
    throw new SelfRegistrationBootstrapError({
      status: 0,
      code: "network_error",
      message: "Network request failed.",
    });
  }

  const parsedBody = await safeParseJson(response);

  if (!response.ok) {
    throw buildApiErrorFromResponse(response.status, parsedBody);
  }

  if (!isPlainObject(parsedBody) || parsedBody.ok !== true) {
    throw buildInvalidResponseError(response.status);
  }

  // Force-refresh so the newly-granted custom claims (role/plan/access)
  // are reflected in the very next getIdTokenResult()/getIdToken() call.
  try {
    await user.getIdToken(true);
  } catch (_e) {
    throw new SelfRegistrationBootstrapError({
      status: 401,
      code: "token_refresh_failed",
      message: "Could not refresh the session after activation.",
    });
  }

  return {
    ok: true,
    bootstrapStatus: isNonEmptyString(parsedBody.bootstrapStatus) ? parsedBody.bootstrapStatus : null,
    plan: isNonEmptyString(parsedBody.plan) ? parsedBody.plan : null,
    role: isNonEmptyString(parsedBody.role) ? parsedBody.role : null,
    access: isPlainObject(parsedBody.access) ? parsedBody.access : null,
  };
}

// =========================================================================
// shouldBootstrapSelfRegistration
// =========================================================================

/**
 * Canonical role read from Firebase custom claims ONLY. Mirrors the
 * backend's own normalizeRoleFromClaims() variants exactly, so this
 * client never disagrees with the server about who counts as
 * privileged.
 *
 * @param {*} claims
 * @returns {"user"|"admin"|"super_admin"}
 */
function normalizeRoleFromClaims(claims) {
  const raw = String((claims && claims.role) || "")
    .trim()
    .toLowerCase();
  if (raw === "super_admin" || raw === "super.admin" || raw === "superadmin" || raw === "super admin") {
    return "super_admin";
  }
  if (raw === "admin") return "admin";
  return "user";
}

function hasCreatorClaimField(claims) {
  if (!claims || typeof claims !== "object") return false;
  for (const field of CREATOR_CLAIM_FIELDS) {
    const v = claims[field];
    if (v != null && String(v).trim() !== "") return true;
  }
  return false;
}

/**
 * Normalizes a Firebase custom-claims `plan` value: trim + lowercase.
 * A missing/null/empty/whitespace-only plan normalizes to "" — kept
 * distinct from an explicit "basic" (see point 6 below), exactly like
 * the backend's own plan handling in classifyBootstrapMode().
 *
 * @param {*} claims
 * @returns {string}
 */
function normalizePlanFromClaims(claims) {
  const raw = claims && typeof claims === "object" ? claims.plan : null;
  if (raw == null) return "";
  return String(raw).trim().toLowerCase();
}

/**
 * True only for an `access` claim shaped exactly like a pre-Phase-M
 * legacy Basic-trial grant — mirrors the backend's own
 * isForeignAccess()/classifyBootstrapMode() "legacy_candidate" shape
 * check (mode/expiresAt/no-source), MINUS the expiresAt-equality
 * check (this client has no way to independently recompute the
 * backend's expected expiresAt, and doesn't need to — the backend's
 * registry is the sole source of truth for whether a legacy grant is
 * actually migrated; this client only flags the account as a
 * *candidate* worth calling the bootstrap route for).
 *
 * Returns false for anything malformed: non-plain-object access, a
 * non-"days" mode, a non-finite expiresAt, or any non-null `source`
 * (including this route's own "self_trial" and adminUsers.js's
 * "admin_grant" — those are never candidates, they are already
 * decided states).
 *
 * @param {*} access
 * @returns {boolean}
 */
function isLegacyTrialCandidateAccess(access) {
  if (!isPlainObject(access)) return false;
  if (access.mode !== "days") return false;
  if (!Number.isFinite(access.expiresAt)) return false;
  if (access.source != null) return false;
  return true;
}

/**
 * True only when the account is safe to call
 * bootstrapSelfRegistrationTrial for — i.e. it is either:
 *
 *   1. Brand-new: no plan, no access, no creator marker, not
 *      privileged/admin (the original, never-bootstrapped account —
 *      the backend's own "new_registration" bucket).
 *   2. A legacy migration candidate: normalized plan === "basic"
 *      exactly, a non-privileged role, and `access` is a plain object
 *      shaped like a pre-Phase-M legacy trial grant (mode "days", a
 *      finite expiresAt, and no `source` marker at all) — the
 *      backend's own "legacy_candidate" bucket. This client only ever
 *      *flags* the candidate; the backend's registry alone decides
 *      whether the migration is actually granted (see
 *      readGrantRegistrySnapshot/evaluateLegacyMigrationCandidate in
 *      authSelfRegistrationBootstrap.js) — this function never
 *      mutates or assumes that outcome.
 *
 * Returns false for every other state, including (non-exhaustive):
 *   - admin === true, or a privileged role (admin/super_admin/
 *     super.admin/superadmin/"super admin"),
 *   - access.source === "self_trial" (already this route's own prior
 *     grant — no need to bootstrap again),
 *   - access.source === "admin_grant" (admin-managed; never touched),
 *   - any other non-null access.source,
 *   - a plan that isn't exactly "basic" and isn't empty (e.g. "pro",
 *     "premium", or any unrecognized value) — never coerced to Basic,
 *   - a malformed `access` (wrong mode, non-finite expiresAt, non-
 *     plain-object) even when plan is "basic",
 *   - any createdBy... / createdByAdmin... creator marker.
 *
 * Never mutates `claims` (or any nested object it references).
 *
 * @param {*} claims
 * @returns {boolean}
 */
function shouldBootstrapSelfRegistration(claims) {
  const safeClaims = claims && typeof claims === "object" ? claims : {};

  if (safeClaims.admin === true) return false;

  const role = normalizeRoleFromClaims(safeClaims);
  if (role === "admin" || role === "super_admin") return false;

  if (hasCreatorClaimField(safeClaims)) return false;

  const plan = normalizePlanFromClaims(safeClaims);
  const access = safeClaims.access;

  if (plan === "" && access == null) {
    return true;
  }

  if (plan === "basic" && isLegacyTrialCandidateAccess(access)) {
    return true;
  }

  return false;
}

module.exports = {
  SELF_REGISTRATION_BOOTSTRAP_ENDPOINT,
  bootstrapSelfRegistrationTrial,
  shouldBootstrapSelfRegistration,
  SelfRegistrationBootstrapError,
};
